import { useEffect, useRef } from 'react';
import { ContentNode } from '@amzn/awscat-pattern-library-service-model';

import { sectionIdForNode, sectionScrollTargetId } from '../v2-common/ContentNodeExtensions';
import { useCurrentSectionId, useNavigateToSection } from './useUrlHash';

interface SectionPosition {
  readonly sectionId: string;
  readonly startY: number;
}

/** Returns the absolute-Y start position of every mounted section, sorted ascending. */
function collectSectionPositions(sections: readonly ContentNode[]): SectionPosition[] {
  const positions: SectionPosition[] = [];
  for (const section of sections) {
    const sectionId = sectionIdForNode(section);
    const element = document.getElementById(sectionScrollTargetId(sectionId));
    if (!element) continue;
    positions.push({ sectionId, startY: element.getBoundingClientRect().top + window.scrollY });
  }
  positions.sort((a, b) => a.startY - b.startY);
  return positions;
}

function getHeaderHeight(): number {
  const header = document.getElementById('page-header-container');
  if (!header) {
    throw new Error('Page header not found');
  }
  return header.getBoundingClientRect().height;
}

/** Returns the section id that should be highlighted for the current window scroll position. */
function pickCurrentSectionId(positions: readonly SectionPosition[]): string | undefined {
  if (positions.length === 0) return undefined;

  // Viewport top edge is the window scroll offset plus the sticky header's rendered height.
  const viewportTop = window.scrollY + getHeaderHeight();
  const viewportBottom = window.scrollY + window.innerHeight;

  // Locate the first section whose start lies below the viewport top edge.
  const nextIndex = positions.findIndex((p) => p.startY > viewportTop);

  // No upcoming section — the user is past every section start, so highlight the last one.
  if (nextIndex < 0) return positions[positions.length - 1]?.sectionId;

  // Upcoming section is visible on screen — treat it as the section the reader is entering.
  const nextSection = positions[nextIndex];
  if (!nextSection) return undefined;
  if (nextSection.startY < viewportBottom) return nextSection.sectionId;

  // Otherwise the section actually occupying the top is the one immediately before, or the first
  // section when nothing has been scrolled past yet.
  const chosen = nextIndex === 0 ? positions[0] : positions[nextIndex - 1];
  return chosen?.sectionId;
}

/**
 * Keeps the URL-selected Pattern section and the window scroll position in sync in both
 * directions: hash changes trigger a smooth scroll to the section, and scroll gestures update
 * the hash to the section currently under the sticky header.
 */
export function useScrollToCurrentPatternSection(sections: readonly ContentNode[]): void {
  const currentSectionId = useCurrentSectionId();
  const navigateToSection = useNavigateToSection();

  // Section id most recently written to the URL by the scroll listener. Used to short-circuit
  // the hash→scroll effect so the reader's organic scroll does not trigger a redundant re-scroll.
  const lastWroteSectionIdRef = useRef<string | undefined>(undefined);
  // Target of an in-flight programmatic smooth scroll. While set, the scroll listener suppresses
  // hash writes so intermediate sections passing under the top edge don't hijack the URL.
  const programmaticTargetIdRef = useRef<string | undefined>(undefined);

  // When current section changes, automatically scroll to it
  useEffect(() => {
    if (currentSectionId === undefined) return;

    // Ignore this currentSectionId change, if it is initiated by the window
    // scroll event below.
    if (lastWroteSectionIdRef.current === currentSectionId) {
      lastWroteSectionIdRef.current = undefined;
      return;
    }

    // Mark a programmatic scroll in-flight; the scroll listener will hold off until we arrive
    // (or the reader interrupts).
    programmaticTargetIdRef.current = currentSectionId;

    const frameId = requestAnimationFrame(() => {
      document
        .getElementById(sectionScrollTargetId(currentSectionId))
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    return () => cancelAnimationFrame(frameId);
  }, [currentSectionId]);

  // When the window scrolls, automatically change the current position
  // (The left hand side and the URL hash are automatically updated,
  // better user experience)
  useEffect(() => {
    if (sections.length === 0) return;

    let pendingFrameId = 0;

    // Considered "arrived" when the top edge is within 2px of the target's start-Y, or at page
    // bottom (target is a last section whose start sits below the max scroll offset), or the
    // target section has unmounted (nothing to wait for).
    const isAtProgrammaticTarget = (positions: readonly SectionPosition[]): boolean => {
      const targetId = programmaticTargetIdRef.current;
      if (targetId === undefined) return true;
      const target = positions.find((p) => p.sectionId === targetId);
      if (!target) return true;
      const distance = Math.abs(window.scrollY + getHeaderHeight() - target.startY);
      const maxScrollY = document.documentElement.scrollHeight - window.innerHeight;
      const atPageBottom = window.scrollY >= maxScrollY - 1;
      return distance <= 2 || atPageBottom;
    };

    const recompute = () => {
      pendingFrameId = 0;

      const positions = collectSectionPositions(sections);

      // Programmatic scroll still in flight — leave the URL alone until we arrive.
      if (!isAtProgrammaticTarget(positions)) return;

      // Programmatic has just finished  — leave the URL alone until the user manually scrolls.
      const justArrived = programmaticTargetIdRef.current !== undefined;
      programmaticTargetIdRef.current = undefined;
      if (justArrived) return;

      const nextSectionId = pickCurrentSectionId(positions);
      if (nextSectionId === undefined || nextSectionId === currentSectionId) return;

      lastWroteSectionIdRef.current = nextSectionId;
      navigateToSection(nextSectionId);
    };

    const handleScroll = () => {
      if (pendingFrameId !== 0) return;
      pendingFrameId = requestAnimationFrame(recompute);
    };

    // Reader-initiated gestures cancel any in-flight smooth scroll; drop suppression too so the
    // URL tracks the reader's new destination immediately.
    const handleUserInterrupt = () => {
      programmaticTargetIdRef.current = undefined;
    };

    // Sync once on mount so a reload with an already-scrolled window reflects the right section.
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('wheel', handleUserInterrupt, { passive: true });
    window.addEventListener('touchstart', handleUserInterrupt, { passive: true });
    window.addEventListener('keydown', handleUserInterrupt);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleUserInterrupt);
      window.removeEventListener('touchstart', handleUserInterrupt);
      window.removeEventListener('keydown', handleUserInterrupt);
      if (pendingFrameId !== 0) cancelAnimationFrame(pendingFrameId);
    };
  }, [sections, currentSectionId, navigateToSection]);
}
